import {Observable, of} from "rxjs";
import {AjaxError, AjaxResponse} from "rxjs/ajax";
import Api from "../../Api";
import Constants from "../../Constants";
import {catchError, map} from "rxjs/operators";
import {StereotypeCreateDto, StereotypeDto, StereotypeUpdateDto} from "./StereotypeDto";
import {
    AttributeDefinitionCreateDto,
    AttributeDefinitionDto,
    AttributeDefinitionUpdateDto
} from "./AttributeDefinitionDto";
import {
    StereotypeLayoutDefinitionCreateDto,
    StereotypeLayoutDefinitionDto,
    StereotypeLayoutDefinitionUpdateDto
} from "./StereotypeLayoutDefinitionDto";
import {
    ReferenceDefinitionCreateDto,
    ReferenceDefinitionDto,
    ReferenceDefinitionUpdateDto, ReferenceElementFilterDto
} from "./ReferenceDefinitionDto";

export class StereotypeApiClient {
    public static readonly ENDPOINT_URL: string = Constants.REST_API_URL + "/stereotypes";

    findAll(): Promise<StereotypeDto[]> {
        const request = {
            url: StereotypeApiClient.ENDPOINT_URL + "/",
            method: "GET",
        };
        return Api.createAjax(request)
                .pipe(
                    map((response) => response.response as StereotypeDto[])
                )
                .toPromise();
    }

    findById(stereotypeId: string): Promise<StereotypeDto | undefined> {
        const request = {
            url: StereotypeApiClient.ENDPOINT_URL + `/${stereotypeId}`,
            method: "GET",
        };
        return Api.createAjax(request)
                .pipe(
                    map((response) => response.response as StereotypeDto),
                    catchError(err => {
                        if (err instanceof AjaxError && err.status === 404) {
                            return of(undefined);
                        } else {
                            throw err;
                        }
                    })
                )
                .toPromise();
    }

    createStereotype(stereotype: StereotypeCreateDto): Promise<any> {
        const request = {
            url: StereotypeApiClient.ENDPOINT_URL + "/",
            method: "POST",
            body: stereotype,
        };
        return Api.createAjax(request).toPromise();
    }

    batchCreateStereotypes(stereotypes: StereotypeCreateDto[]): Promise<string[]> {
        const request = {
            url: StereotypeApiClient.ENDPOINT_URL + ":batch-create",
            method: "POST",
            body: {
                stereotypes: stereotypes
            },
        };
        return Api.createAjax(request)
                .pipe(
                    map((response) => response.response as string[])
                )
                .toPromise();

    }

    updateStereotype(id: string, stereotypeUpdate: StereotypeUpdateDto): Promise<void> {
        const request = {
            url: StereotypeApiClient.ENDPOINT_URL + `/${id}`,
            method: "PATCH",
            body: stereotypeUpdate,
        };
        return Api.createAjax(request)
                .pipe(
                        map((response) => {})
                )
                .toPromise();
    }

    deleteStereotypeById(id: string): Observable<AjaxResponse> {
        return Api.createAjax({
            url: `${StereotypeApiClient.ENDPOINT_URL}/${id}`,
            method: "DELETE",
        });
    }

    findAllAttributes(stereotypeId: string): Promise<AttributeDefinitionDto[]> {
        const request = {
            url: StereotypeApiClient.ENDPOINT_URL + `/${stereotypeId}/attributes`,
            method: "GET",
        };
        return Api.createAjax(request)
                .pipe(
                    map((response) => response.response)
                )
                .toPromise();
    }

    createAttribute(stereotypeId: string, attributeCreate: AttributeDefinitionCreateDto): Promise<string> {
        const request = {
            url: StereotypeApiClient.ENDPOINT_URL + `/${stereotypeId}/attributes`,
            method: "POST",
            body: attributeCreate
        };
        return Api.createAjax(request)
            .pipe(
                map((response) => response.response as string)
            )
            .toPromise();
    }

    updateAttribute(stereotypeId: string, attributeId: string, attributeUpdate: AttributeDefinitionUpdateDto): Promise<void> {
        const request = {
            url: StereotypeApiClient.ENDPOINT_URL + `/${stereotypeId}/attributes/${attributeId}`,
            method: "PATCH",
            body: attributeUpdate
        };
        return Api.createAjax(request)
            .pipe(
                map((response) => {})
            )
            .toPromise();
    }

    deleteAttribute(stereotypeId: string, attributeId: string): Promise<void> {
        const request = {
            url: StereotypeApiClient.ENDPOINT_URL + `/${stereotypeId}/attributes/${attributeId}`,
            method: "DELETE",
        };
        return Api.createAjax(request)
            .pipe(
                map((response) => {})
            )
            .toPromise();
    }

    findAllRefDefinitions(stereotypeId: string): Promise<ReferenceDefinitionDto[]> {
        const request = {
            url: StereotypeApiClient.ENDPOINT_URL + `/${stereotypeId}/reference-definitions`,
            method: "GET",
        };
        return Api.createAjax(request)
                .pipe(
                        map((response) => response.response)
                )
                .toPromise();
    }

    findReferenceDefinitionById(stereotypeId: string, referenceDefinitionId: string): Promise<ReferenceDefinitionDto | undefined> {
        const request = {
            url: StereotypeApiClient.ENDPOINT_URL + `/${stereotypeId}/reference-definitions/${referenceDefinitionId}`,
            method: "GET",
        };
        return Api.createAjax(request)
                .pipe(
                        map((response) => response.response),
                        catchError(err => {
                            if (err instanceof AjaxError && err.status === 404) {
                                return of(undefined);
                            } else {
                                throw err;
                            }
                        })
                )
                .toPromise();
    }

    createReference(stereotypeId: string, referenceCreate: ReferenceDefinitionCreateDto): Promise<string> {
        const request = {
            url: StereotypeApiClient.ENDPOINT_URL + `/${stereotypeId}/reference-definitions`,
            method: "POST",
            body: referenceCreate
        };
        return Api.createAjax(request)
                .pipe(
                        map((response) => response.response as string)
                )
                .toPromise();
    }

    updateReference(stereotypeId: string, referenceId: string, referenceUpdate: ReferenceDefinitionUpdateDto): Promise<void> {
        const request = {
            url: StereotypeApiClient.ENDPOINT_URL + `/${stereotypeId}/reference-definitions/${referenceId}`,
            method: "PATCH",
            body: referenceUpdate
        };
        return Api.createAjax(request)
                .pipe(
                        map(() => {})
                )
                .toPromise();
    }

    updateReferenceElementFilter(stereotypeId: string, referenceId: string, elementFilter: ReferenceElementFilterDto): Promise<void> {
        const request = {
            url: StereotypeApiClient.ENDPOINT_URL + `/${stereotypeId}/reference-definitions/${referenceId}/related-element-filter`,
            method: "PUT",
            body: elementFilter
        };
        return Api.createAjax(request)
                .pipe(
                        map(() => {})
                )
                .toPromise();
    }

    deleteReference(stereotypeId: string, referenceId: string): Promise<void> {
        const request = {
            url: StereotypeApiClient.ENDPOINT_URL + `/${stereotypeId}/reference-definitions/${referenceId}`,
            method: "DELETE",
        };
        return Api.createAjax(request)
                .pipe(
                        map(() => {})
                )
                .toPromise();
    }

    findLayout(stereotypeId: string, layoutId: string): Promise<StereotypeLayoutDefinitionDto | null> {
        const request = {
            url: StereotypeApiClient.ENDPOINT_URL + `/${stereotypeId}/layout-definition/${layoutId}`,
            method: "GET",
        };
        return Api.createAjax(request)
            .pipe(
                map((response) => response.response as StereotypeLayoutDefinitionDto),
                catchError(err => {
                    if (err instanceof AjaxError && err.status === 404) {
                        return of(null);
                    } else {
                        throw err;
                    }
                })
            )
            .toPromise();
    }

    createLayout(stereotypeId: string, layoutCreate: StereotypeLayoutDefinitionCreateDto): Promise<string> {
        const request = {
            url: StereotypeApiClient.ENDPOINT_URL + `/${stereotypeId}/layout-definition`,
            method: "POST",
            body: layoutCreate
        };
        return Api.createAjax(request)
                .pipe(map((response) => response.response as string))
                .toPromise();
    }

    updateLayout(stereotypeId: string, layoutId: string, layoutUpdate: StereotypeLayoutDefinitionUpdateDto): Promise<void> {
        const request = {
            url: StereotypeApiClient.ENDPOINT_URL + `/${stereotypeId}/layout-definition/${layoutId}`,
            method: "PUT",
            body: layoutUpdate
        };
        return Api.createAjax(request)
                .pipe(map(() => {}))
                .toPromise();
    }

    deleteLayout(stereotypeId: string, layoutId: string): Promise<void> {
        const request = {
            url: StereotypeApiClient.ENDPOINT_URL + `/${stereotypeId}/layout-definition/${layoutId}`,
            method: "DELETE",
        };
        return Api.createAjax(request)
                .pipe(map(() => {}))
                .toPromise();
    }
}

export default new StereotypeApiClient();
